import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Briefcase, ChevronRight, Download } from 'lucide-react';

const ExperienceCard = ({ exp, index, isLast }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: -32 }}
      animate={isInView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1, ease: 'easeOut' }}
      className="relative flex gap-5"
    >
      {/* Timeline column */}
      <div className="flex flex-col items-center flex-shrink-0">
        <div className="w-10 h-10 rounded-full bg-violet-500/10 border border-violet-500/30 flex items-center justify-center text-violet-400 z-10">
          <Briefcase className="w-4 h-4" />
        </div>
        {!isLast && (
          <div className="w-px flex-1 bg-gradient-to-b from-violet-500/25 to-transparent mt-2 min-h-[24px]" />
        )}
      </div>

      {/* Card */}
      <div className={`pb-10 flex-1 ${isLast ? 'pb-0' : ''}`}>
        <div className="group bg-slate-800 border border-slate-700 rounded-xl p-6 hover:border-slate-600 transition-all duration-300">
          <div className="flex flex-wrap justify-between items-start gap-3 mb-4">
            <div>
              <h3 className="text-base font-bold text-slate-100">{exp.title}</h3>
              <p className="text-violet-400 text-sm font-medium mt-0.5">{exp.company}</p>
            </div>
            <span className="text-xs font-medium text-slate-400 bg-slate-700 px-3 py-1.5 rounded-full whitespace-nowrap">
              {exp.period}
            </span>
          </div>

          <ul className="space-y-2">
            {exp.description.map((item, idx) => (
              <li key={idx} className="flex items-start gap-2 text-slate-400 text-sm leading-relaxed">
                <ChevronRight className="w-4 h-4 text-cyan-500/80 mt-0.5 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
};

const WorkExperience = () => {
  const titleRef = useRef(null);
  const titleInView = useInView(titleRef, { once: true });

  const experiences = [
    {
      title: 'Senior Quality Engineer',
      company: 'S&P Global',
      period: '2025 - Current',
      description: [
        'Leading quality assurance for AI-powered financial data platform features on Capital IQ Pro',
        'Testing and evaluating LLM responses using Langfuse — tracking prompt performance and AI quality metrics',
        'Building and validating AI agent workflows with LangGraph for multi-step reasoning pipelines',
        'Integrating and testing MCP (Model Context Protocol) tool connections for AI assistant capabilities',
        'Creating comprehensive test automation strategies using Gherkin syntax and Playwright',
        'Collaborating with cross-functional teams to maintain quality standards across AI and data analytics features',
      ],
    },
    {
      title: 'QA Engineer',
      company: 'Acoustic',
      period: '2024 - 2025',
      description: [
        'Managing entire testing process for marketing applications (web & API)',
        'Creating test strategies and automation using Java (Selenium) and JavaScript (Playwright)',
        'Building complete test projects with Cucumber and AWS technologies',
        'Presenting test results to stakeholders and overseeing testing lifecycle',
      ],
    },
    {
      title: 'Software Test Engineer',
      company: 'Solwit S.A (Intel Poland)',
      period: '2021 - 2024',
      description: [
        'Led performance testing department for OpenVINO applications',
        'Developed Python tools for validation procedures enhancement',
        'Resolved CI errors within Docker and Jenkins environments',
        'Designed and maintained test repository in Python (Selenium, pytest)',
      ],
    },
    {
      title: 'Junior Specialist for Testing',
      company: 'ManpowerGroup Poland (Intel Poland)',
      period: '2020 - 2021',
      description: [
        'Established Windows test environments',
        'Executed thorough testing procedures',
        'Produced detailed bug reports and collaborated with development teams',
      ],
    },
  ];

  return (
    <section className="py-24 px-6 bg-slate-900" id="experience">
      <div className="max-w-3xl mx-auto">
        <motion.div
          ref={titleRef}
          initial={{ opacity: 0, y: 20 }}
          animate={titleInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-violet-400 text-xs font-semibold tracking-[0.2em] uppercase mb-3">
            Career
          </p>
          <h2 className="text-4xl font-bold text-slate-100 mb-4">Work Experience</h2>
          <div className="w-16 h-px bg-gradient-to-r from-violet-500 to-cyan-400 mx-auto mb-8" />
          <button
            onClick={() => window.open('/Kajetan Jedrych.pdf', '_blank')}
            className="group inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white px-5 py-2.5 rounded-full font-medium text-sm transition-all duration-300 hover:shadow-lg hover:shadow-violet-500/25 hover:scale-105 active:scale-95"
          >
            <Download className="w-4 h-4" />
            Download Full CV
          </button>
        </motion.div>

        <div>
          {experiences.map((exp, index) => (
            <ExperienceCard
              key={index}
              exp={exp}
              index={index}
              isLast={index === experiences.length - 1}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default WorkExperience;
